// src/components/inventory/CategoriesTab.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • FlowSense per category (pass `intel` — the AI overview): items, stock value, items to
//     re-order and out of stock; items without a category are counted.
//   • Live: reloads when a category or item changes anywhere (WebSocket inventory_update).
//   • Search; sort by name / items / value; delete warns when the category still holds items.
//   • Read-only users (`canManage={false}`) do not see Add / Edit / Delete.
//   • FlowSense AI bar: explain categories, the Ministry of Works explanation, Ask FlowSense.
//   • Error state with Retry and an empty state.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  useColorModeValue,
  Spinner,
  Text,
  Flex,
} from '@chakra-ui/react';
import { HStack, Input, InputGroup, InputLeftElement, Icon, Badge, Select, Alert, AlertIcon, Tooltip, VStack } from '@chakra-ui/react'; // --- UPDATE
import { MdSearch, MdCategory, MdAdd } from 'react-icons/md'; // --- UPDATE
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import inventoryCategoryService from '../../services/inventoryCategoryService';
import CategoryFormModal from './CategoryFormModal';
// --- UPDATE (MOW+ v2)
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { categoryRollup, money } from './inventoryAiIntel';
// --- END UPDATE

const CategoriesTab = ({ intel = null, canManage = true, command = null }) => { // --- UPDATE: + intel, canManage, command (quick actions)
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // --- UPDATE
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [error, setError] = useState(null);
  // --- END UPDATE
  const toast = useToast();

  const bgColor = useColorModeValue("white", "navy.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const mutedColor = useColorModeValue("gray.500", "gray.400"); // --- UPDATE
  const headBg = useColorModeValue("gray.50", "whiteAlpha.50"); // --- UPDATE

  const fetchCategories = async (quiet = false) => { // --- UPDATE: + quiet
    if (!quiet) setIsLoading(true);
    try {
      const data = await inventoryCategoryService.getAllCategories();
      setCategories(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []); // --- UPDATE
      setError(null); live.markUpdated(); // --- UPDATE
    } catch (error) {
      setError(error.message || 'Could not load categories.'); // --- UPDATE
      if (!quiet) toast({
        title: 'Error fetching categories.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- UPDATE: live
  const live = useInventoryLive(() => fetchCategories(true), { events: ['inventory_update'] });
  // --- END UPDATE

  useEffect(() => {
    fetchCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- UPDATE: FlowSense roll-up + search / sort
  const rollup = React.useMemo(() => categoryRollup(categories, intel?.items || null), [categories, intel]);
  const shown = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const r = rollup.byName;
    return categories.filter((c) => !q || `${c.name} ${c.description || ''}`.toLowerCase().includes(q))
      .sort((a, b) => (sortBy === 'items' ? (r[b.name]?.items || 0) - (r[a.name]?.items || 0)
        : sortBy === 'value' ? (r[b.name]?.value || 0) - (r[a.name]?.value || 0)
          : String(a.name).localeCompare(String(b.name))));
  }, [categories, search, sortBy, rollup]);
  const top = React.useMemo(() => Object.entries(rollup.byName).filter(([, v]) => v.hasValue).sort((a, b) => b[1].value - a[1].value)[0] || null, [rollup]);
  const reorderCats = Object.values(rollup.byName).filter((v) => v.below_reorder > 0).length;
  // --- END UPDATE

  // --- UPDATE: quick action "Add Category"
  useEffect(() => { if (command?.type === 'addCategory' && canManage) { setSelectedCategory(null); setIsModalOpen(true); } }, [command]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenModal = (category = null) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    // --- UPDATE: warn when the category still holds items
    const cat = categories.find((c) => c.id === id);
    const held = cat ? rollup.byName[cat.name]?.items || 0 : 0;
    const msg = held ? `"${cat.name}" still holds ${held} item(s). Deleting it leaves them without a category. Delete anyway?` : 'Are you sure you want to delete this category? This may affect existing inventory items.';
    // --- END UPDATE
    if (window.confirm(msg)) {
      try {
        await inventoryCategoryService.deleteCategory(id);
        toast({
          title: 'Category deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchCategories();
      } catch (error) {
        toast({
          title: 'Error deleting category.',
          description: error?.message, // --- UPDATE: server message (e.g. still in use)
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Box bg={bgColor} p={4} borderRadius="lg" boxShadow="md">
      {/* --- UPDATE: FlowSense AI */}
      <Box mb={4}>
        <FlowSenseInsightBar screen="categories" data={intel}
          headline={intel ? [`${categories.length} categories · ${rollup.uncategorised ?? '—'} items without a category`, top ? `Highest value: ${top[0]} (${money(top[1].value)}) · ${reorderCats} categor${reorderCats === 1 ? 'y has' : 'ies have'} items to re-order` : 'No priced items in any category yet.'] : `${categories.length} categories`}
          extra={{ categories: categories.length, uncategorised: rollup.uncategorised, by_category: Object.fromEntries(Object.entries(rollup.byName).slice(0, 30)) }}
          rulesAnswer={(q) => (/categor/i.test(q) ? ['Categories — FlowSense roll-up:', ...Object.entries(rollup.byName).sort((a, b) => b[1].value - a[1].value).slice(0, 10).map(([n, v]) => `• ${n}: ${v.items} items, ${v.hasValue ? money(v.value) : '—'}${v.below_reorder ? `, ${v.below_reorder} to re-order` : ''}`), rollup.uncategorised ? `${rollup.uncategorised} items have no category.` : null].filter(Boolean).join('\n') : null)}
          questions={['Which category is worth the most?', 'Which categories need re-ordering?', 'Explain the inventory system for the Ministry of Works']} />
      </Box>
      {/* --- END UPDATE */}
      <Flex justifyContent="space-between" alignItems="center" mb={4} gap={3} flexWrap="wrap">
        <HStack spacing={3}>{/* --- UPDATE */}
          <Text fontSize="xl" fontWeight="bold">Manage Categories</Text>
          <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} />
        </HStack>
        {/* --- UPDATE: search / sort */}
        <HStack spacing={2} flex={1} justify="flex-end" flexWrap="wrap">
          <InputGroup size="sm" maxW="240px"><InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input placeholder="Search categories…" value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="lg" aria-label="Search categories" /></InputGroup>
          <Select size="sm" maxW="150px" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort categories" borderRadius="lg">
            <option value="name">Sort: name</option><option value="items">Sort: items</option><option value="value">Sort: value</option>
          </Select>
        {canManage && ( /* --- UPDATE */
        <Button colorScheme="blue" onClick={() => handleOpenModal()} leftIcon={<Icon as={MdAdd} />}>
          Add Category
        </Button>
        )}
        </HStack>
      </Flex>

      {error && <Alert status="error" borderRadius="lg" mb={3}><AlertIcon />{error}<Button size="sm" ml="auto" onClick={() => fetchCategories()}>Retry</Button></Alert>}{/* --- UPDATE */}

      {isLoading ? (
        <Flex justifyContent="center" p={10}><Spinner /></Flex>
      ) : !shown.length ? ( /* --- UPDATE: empty state */
        <VStack p={10} color={mutedColor}><Icon as={MdCategory} boxSize={10} /><Text>{categories.length ? 'No categories match the search.' : 'No categories yet — add one to group your items.'}</Text></VStack>
      ) : (
        <Box overflowX="auto">
        <Table variant="simple" aria-label="Categories">
          <Thead bg={headBg}>
            <Tr>
              <Th borderColor={borderColor}>Name</Th>
              <Th borderColor={borderColor}>Description</Th>
              {/* --- UPDATE: FlowSense columns */}
              <Th borderColor={borderColor} isNumeric>Items</Th>
              <Th borderColor={borderColor} isNumeric>Value</Th>
              <Th borderColor={borderColor}>FlowSense</Th>
              {canManage && <Th borderColor={borderColor}>Actions</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {shown.map((category) => { /* --- UPDATE: filtered + sorted */
              const r = rollup.byName[category.name]; // --- UPDATE
              return (
              <Tr key={category.id}>
                <Td borderColor={borderColor} fontWeight="600">{category.name}</Td>
                <Td borderColor={borderColor} color={mutedColor} fontSize="sm">{category.description}</Td>
                {/* --- UPDATE */}
                <Td borderColor={borderColor} isNumeric>{intel ? (r?.items ?? 0) : '—'}</Td>
                <Td borderColor={borderColor} isNumeric>{intel && r?.hasValue ? money(r.value) : '—'}</Td>
                <Td borderColor={borderColor}>
                  {!intel ? <Text fontSize="xs" color={mutedColor}>—</Text> : (
                    <HStack spacing={1}>
                      {r?.stockouts ? <Tooltip hasArrow label="Items with zero on hand"><Badge colorScheme="red">{r.stockouts} out</Badge></Tooltip> : null}
                      {r?.below_reorder ? <Tooltip hasArrow label="Items below the FlowSense re-order point"><Badge colorScheme="orange">{r.below_reorder} to re-order</Badge></Tooltip> : null}
                      {!r?.below_reorder && !r?.stockouts ? <Badge colorScheme="green">OK</Badge> : null}
                    </HStack>
                  )}
                </Td>
                {canManage && (
                <Td borderColor={borderColor}>
                  <IconButton
                    icon={<EditIcon />}
                    aria-label={`Edit Category ${category.name}`}
                    variant="ghost"
                    onClick={() => handleOpenModal(category)}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    aria-label={`Delete Category ${category.name}`}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete(category.id)}
                  />
                </Td>
                )}
                {/* --- END UPDATE */}
              </Tr>
              );
            })}
          </Tbody>
        </Table>
        </Box>
      )}

      {isModalOpen && (
        <CategoryFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          category={selectedCategory}
          onSave={fetchCategories}
          existingNames={categories.filter((c) => !selectedCategory || c.id !== selectedCategory.id).map((c) => c.name)} // --- UPDATE: duplicate check
        />
      )}
    </Box>
  );
};

export default CategoriesTab;
